import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { App } from 'antd'
import { useTranslation } from 'react-i18next'

import {
  mcpConnectionsListOptions,
  mcpConnectionsListQueryKey,
  mcpConnectionsRevoke,
  mcpConnectionsScopesUpdate,
  mcpMyConnectionsListOptions,
  mcpMyConnectionsListQueryKey,
  mcpMyConnectionsRevoke,
  mcpMyConnectionsScopesUpdate,
  mcpMyServicesListOptions,
  mcpMyServicesListQueryKey,
  mcpPersonalTokensCreate,
  mcpPoliciesListOptions,
  mcpPoliciesListQueryKey,
  mcpPoliciesPartialUpdate
} from 'src/api/main'
import { useShowApiErrors } from 'src/utils'

import type {
  ConnectionsScope,
  McpConnectionFilters,
  McpPersonalTokenCreate,
  McpServiceId,
  McpServicePolicyPatch
} from './types'

const isValidWorkspace = (workspaceId: number) =>
  Number.isFinite(workspaceId) && workspaceId > 0

export const useMcpPolicies = (workspaceId: number, enabled: boolean) =>
  useQuery({
    ...mcpPoliciesListOptions({ path: { organization_id: workspaceId } }),
    enabled: enabled && isValidWorkspace(workspaceId),
    refetchOnMount: 'always'
  })

export const useMyMcpServices = (workspaceId: number) =>
  useQuery({
    ...mcpMyServicesListOptions({ query: { organization_id: workspaceId } }),
    enabled: isValidWorkspace(workspaceId),
    refetchOnMount: 'always'
  })

export const useMcpConnections = (
  workspaceId: number,
  scope: ConnectionsScope,
  filters: McpConnectionFilters
) => {
  const valid = isValidWorkspace(workspaceId)
  const mine = useQuery({
    ...mcpMyConnectionsListOptions({
      query: { organization_id: workspaceId }
    }),
    enabled: valid && scope === 'mine',
    refetchOnMount: 'always'
  })
  const org = useQuery({
    ...mcpConnectionsListOptions({
      path: { organization_id: workspaceId },
      query: filters
    }),
    enabled: valid && scope === 'org',
    refetchOnMount: 'always',
    // A filter change keeps the table (and its filters) on screen while it loads.
    placeholderData: keepPreviousData
  })

  return scope === 'mine' ? mine : org
}

/** Polled while the Connect wizard waits for a client to finish signing in. */
export const useMyMcpConnections = (workspaceId: number, poll: boolean) =>
  useQuery({
    ...mcpMyConnectionsListOptions({
      query: { organization_id: workspaceId }
    }),
    enabled: isValidWorkspace(workspaceId),
    refetchInterval: poll ? 3000 : false
  })

export const useInvalidateMcpAccess = (workspaceId: number) => {
  const queryClient = useQueryClient()

  return () => {
    // Keys without `query` match every filtered variant (partial key match).
    for (const queryKey of [
      mcpPoliciesListQueryKey({ path: { organization_id: workspaceId } }),
      mcpMyServicesListQueryKey({ query: { organization_id: workspaceId } }),
      mcpMyConnectionsListQueryKey({
        query: { organization_id: workspaceId }
      }),
      mcpConnectionsListQueryKey({ path: { organization_id: workspaceId } })
    ]) {
      void queryClient.invalidateQueries({ queryKey })
    }
  }
}

export const useUpdateMcpPolicy = (workspaceId: number) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const invalidate = useInvalidateMcpAccess(workspaceId)

  return useMutation({
    mutationFn: ({
      service,
      body
    }: {
      service: McpServiceId
      body: McpServicePolicyPatch
    }) =>
      mcpPoliciesPartialUpdate({
        path: { organization_id: workspaceId, service },
        body,
        throwOnError: true
      }).then(({ data }) => data),
    onSuccess: () => {
      message.success(t('workspace.aiConnectors.servicesTab.saved'))
      invalidate()
    },
    onError: (error) => showApiErrors(error)
  })
}

export const useRevokeMcpConnection = (
  workspaceId: number,
  scope: ConnectionsScope
) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const invalidate = useInvalidateMcpAccess(workspaceId)

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      (scope === 'mine'
        ? mcpMyConnectionsRevoke({
            path: { id },
            body: { reason },
            throwOnError: true
          })
        : mcpConnectionsRevoke({
            path: { organization_id: workspaceId, id },
            body: { reason },
            throwOnError: true
          })
      ).then(({ data }) => data),
    onSuccess: () => {
      message.success(t('workspace.aiConnectors.connections.revoked'))
      invalidate()
    },
    onError: (error) => showApiErrors(error)
  })
}

export const useUpdateMcpConnectionScopes = (
  workspaceId: number,
  scope: ConnectionsScope
) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const invalidate = useInvalidateMcpAccess(workspaceId)

  return useMutation({
    mutationFn: ({ id, scopes }: { id: string; scopes: string }) =>
      (scope === 'mine'
        ? mcpMyConnectionsScopesUpdate({
            path: { id },
            body: { scopes },
            throwOnError: true
          })
        : mcpConnectionsScopesUpdate({
            path: { organization_id: workspaceId, id },
            body: { scopes },
            throwOnError: true
          })
      ).then(({ data }) => data),
    onSuccess: () => {
      message.success(t('workspace.aiConnectors.connections.scopesSaved'))
      invalidate()
    },
    onError: (error) => showApiErrors(error)
  })
}

/** Errors are left to the form, so Core's field errors land on its inputs. */
export const useCreateMcpPersonalToken = (workspaceId: number) => {
  const invalidate = useInvalidateMcpAccess(workspaceId)

  return useMutation({
    mutationFn: (body: McpPersonalTokenCreate) =>
      mcpPersonalTokensCreate({ body, throwOnError: true }).then(
        ({ data }) => data
      ),
    // The plaintext token must not linger in the mutation cache.
    gcTime: 0,
    onSuccess: () => invalidate()
  })
}
